// MARK://dhk8/admin-shell/pages/dev/ComponentsGallery
// Galería viva de la librería de componentes NintaiGO — compuerta de revisión
// (Fase DS-3). Ruta dev /dev/components, NO producción. Reestructurada sobre el
// mini-framework de brands/nintaigo/docs/design-system.md §5: registro de
// secciones + sidebar por categorías + render perezoso de la sección activa.
// Cubre los 25 componentes de apps/admin-shell/src/components + una sección
// "Por revisar" (DS-4: duplicados a unificar).
import { useState } from 'react'

import { CatalogShell, CatalogBlock, CatalogGroup, type CatalogSection } from './catalog-shell.js'

import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import Skeleton, { SkeletonText } from '../../components/Skeleton.js'
import Tabs from '../../components/Tabs.js'
import StatCard, { SummaryStrip } from '../../components/StatCard.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import Sheet from '../../components/Sheet.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import ImageUploader, { ThumbnailCell } from '../../components/ImageUploader.js'
import InlineEdit from '../../components/InlineEdit.js'
import SatCodePicker from '../../components/SatCodePicker.js'
import UnitPicker from '../../components/UnitPicker.js'
import TaxRatePicker from '../../components/TaxRatePicker.js'
import MoneyInput from '../../components/MoneyInput.js'
import BrandMark from '../../components/BrandMark.js'
import ThemeSwitcher from '../../components/ThemeSwitcher.js'
import { ErrorBoundary } from '../../components/ErrorBoundary.js'
import FieldHelp from '../../components/FieldHelp.js'
import { ShareLinkButton } from '../../components/ShareLinkButton.js'
import AuditTrail from '../../components/AuditTrail.js'

type Row = { id: string; name: string; cat: string; price: number; active: boolean }
const SAMPLE: Row[] = Array.from({ length: 7 }, (_, i) => ({
  id: `p${i}`, name: ['Latte', 'Capuchino', 'Americano', 'Malteada', 'Pan', 'Café en grano', 'Leche'][i],
  cat: ['Bebidas', 'Bebidas', 'Bebidas', 'Postres', 'Panadería', 'Insumo', 'Insumo'][i],
  price: [4500, 5600, 3700, 7500, 3500, 0, 0][i], active: i % 3 !== 0,
}))

// ── Layout/Shell ─────────────────────────────────────────────────────────────

function SecAdminLayout() {
  return (
    <CatalogBlock title="AdminLayout — topbar + rail (miniatura)" note="El shell real ya envuelve esta página; aquí una miniatura estática (sin navegación) reusa las MISMAS clases CSS que inyecta AdminLayout.">
      <p className="adm-muted" style={{ margin: 0 }}>
        Topbar: marca + tag + centro (env switch / impersonación) + tema + usuario. Rail: nav agrupada por dominio con separadores, activo = fill redondeado (sin edge-strip). Ver AdminLayout en vivo: esta página misma.
      </p>
      <div style={{ width: '100%', maxWidth: 640, height: 300, border: '1px solid var(--pos-separator-strong)', borderRadius: 'var(--pos-radius-lg)', overflow: 'hidden', position: 'relative' }}>
        <div className="pos-shell" style={{ height: '100%' }}>
          <header className="pos-topbar">
            <div className="pos-topbar__brand">
              <BrandMark size="sm" />
              <span className="pos-topbar__tag">restaurante</span>
            </div>
            <div className="pos-topbar__center">
              <span className="adm-badge adm-badge--warn">PRUEBAS</span>
            </div>
            <div className="pos-topbar__meta">
              <span className="pos-topbar__id"><span className="pos-topbar__user">Ana Dueña</span></span>
            </div>
          </header>
          <div className="pos-body">
            <nav className="pos-rail" aria-hidden>
              <div className="pos-rail__item pos-rail__item--active"><span className="pos-rail__label">Panel</span></div>
              <div className="pos-rail__sep" />
              <div className="pos-rail__item"><span className="pos-rail__label">Catálogo</span></div>
              <div className="pos-rail__item"><span className="pos-rail__label">Inventario</span></div>
            </nav>
            <main className="pos-content"><p className="adm-muted" style={{ margin: 0 }}>Contenido de la página…</p></main>
          </div>
        </div>
      </div>
    </CatalogBlock>
  )
}

function SecEntityDetailPage() {
  const [tab, setTab] = useState('gen')
  return (
    <CatalogBlock title="EntityDetailPage — ficha «ver-primero»">
      <div className="adm-card adm-card--pad0" style={{ overflow: 'hidden' }}>
        <EntityDetailPage
          title="Latte"
          subtitle="SKU LATTE-001 · Bebidas"
          pills={<><StatusPill tone="ok" label="Activo" /><StatusPill tone="info" label="Con receta" /></>}
          kpis={<SummaryStrip items={[{ label: 'Precio', value: '$45.00' }, { label: 'Costo', value: '$12.30' }, { label: 'Margen', value: '72%', tone: 'ok' }, { label: 'Se puede preparar', value: 357 }]} />}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm">Editar</button>}
          tabs={<Tabs tabs={[{ id: 'gen', label: 'General' }, { id: 'rec', label: 'Receta' }, { id: 'precio', label: 'Precios' }]} active={tab} onChange={setTab} ariaLabel="ficha" />}
        >
          <p style={{ color: 'var(--pos-fg-secondary)' }}>Cuerpo de la ficha (contenido del tab “{tab}”).</p>
        </EntityDetailPage>
      </div>
    </CatalogBlock>
  )
}

// ── Overlays ─────────────────────────────────────────────────────────────────

function SecConfirmDialog() {
  const confirm = useConfirm()
  const { toast } = useToast()
  return (
    <CatalogBlock title="ConfirmDialog — reemplazo de window.confirm" note="Solo existe como useConfirm() + <ConfirmProvider> (montado en App.tsx) — no hay un componente <ConfirmDialog> instanciable por separado; click para verlo en vivo.">
      <CatalogGroup label="Promesa (useConfirm) — tone default / danger">
        <button className="adm-btn" onClick={async () => { const ok = await confirm({ title: '¿Continuar?', message: 'Esta acción no tiene tono destructivo.' }); toast(ok ? 'Confirmado' : 'Cancelado', ok ? 'success' : 'info') }}>confirm() tone=default</button>
        <button className="adm-btn adm-btn--danger" onClick={async () => { const ok = await confirm({ title: '¿Eliminar producto?', message: 'No se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' }); toast(ok ? 'Confirmado' : 'Cancelado', ok ? 'success' : 'info') }}>confirm() tone=danger</button>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecSheet() {
  const [open, setOpen] = useState(false)
  return (
    <CatalogBlock title="Sheet — bottom-sheet / modal centrado">
      <button className="adm-btn" onClick={() => setOpen(true)}>Abrir Sheet</button>
      <Sheet open={open} title="Detalle rápido" onClose={() => setOpen(false)} footer={<button className="adm-btn adm-btn--primary" onClick={() => setOpen(false)}>Cerrar</button>}>
        <p style={{ margin: 0, color: 'var(--pos-fg-secondary)' }}>Contenido libre del Sheet — mismo patrón que usan FieldHelp y UnitPicker (crear unidad).</p>
      </Sheet>
    </CatalogBlock>
  )
}

function SecSectionedSheet() {
  const [open, setOpen] = useState(false)
  const [unit, setUnit] = useState('GR')
  const { toast } = useToast()
  return (
    <CatalogBlock title="SectionedSheet — formulario largo en secciones">
      <button className="adm-btn" onClick={() => setOpen(true)}>Abrir SectionedSheet</button>
      <SectionedSheet open={open} title="Nuevo producto (demo seccionado)" onClose={() => setOpen(false)} footer={<><button className="adm-btn" onClick={() => setOpen(false)}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={() => { setOpen(false); toast('Guardado', 'success') }}>Guardar</button></>}>
        <FormSection title="Identidad"><label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" placeholder="Latte" /></label></FormSection>
        <FormSection title="Precio e impuestos"><label className="adm-field"><span className="adm-field__label">Unidad</span><UnitPicker value={unit} onChange={setUnit} bare /></label></FormSection>
      </SectionedSheet>
    </CatalogBlock>
  )
}

// ── Tablas/Datos ─────────────────────────────────────────────────────────────

function SecDataTable() {
  const { toast } = useToast()
  const [sort, setSort] = useState<SortState | undefined>({ key: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const cols: DataTableColumn<Row>[] = [
    { key: 'name', header: 'Nombre', sortable: true, render: (r) => r.name },
    { key: 'cat', header: 'Categoría', sortable: true, render: (r) => r.cat },
    { key: 'price', header: 'Precio', align: 'right', sortable: true, render: (r) => <span className="adm-mono">${(r.price / 100).toFixed(2)}</span> },
    { key: 'estado', header: 'Estado', render: (r) => <StatusPill tone={lifecycleTone(r.active)} label={r.active ? 'Activo' : 'Inactivo'} /> },
  ]
  return (
    <CatalogBlock title="DataTable (nui-table) — con toolbar, orden y paginación">
      <CatalogGroup>
        <div style={{ width: '100%' }}>
          <DataTable<Row>
            columns={cols}
            rows={SAMPLE.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))}
            getRowKey={(r) => r.id}
            zebra
            sort={sort}
            onSortChange={setSort}
            page={page}
            pageSize={5}
            total={42}
            onPageChange={setPage}
            onRowClick={(r) => toast(`Fila: ${r.name}`, 'info')}
            toolbar={<FilterBar search={search} onSearch={setSearch} searchPlaceholder="Buscar producto…" actions={<button className="adm-btn adm-btn--primary adm-btn--sm">+ Nuevo</button>} />}
          />
        </div>
      </CatalogGroup>
      <CatalogGroup label="Estados — loading / vacío">
        <div style={{ flex: 1, minWidth: 280 }}><DataTable<Row> columns={cols} rows={[]} getRowKey={(r) => r.id} loading /></div>
        <div style={{ flex: 1, minWidth: 280 }}><DataTable<Row> columns={cols} rows={[]} getRowKey={(r) => r.id} emptyText="Sin resultados" /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecFilterBar() {
  const [search, setSearch] = useState('')
  const [cat, setCat] = useState('')
  const [estado, setEstado] = useState('')
  return (
    <CatalogBlock title="FilterBar — búsqueda + selects + acciones">
      <CatalogGroup label="Completa (búsqueda + 2 filtros + acción)">
        <div style={{ width: '100%' }}>
          <FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar producto…"
            filters={[
              { key: 'cat', label: 'Categoría', value: cat, onChange: setCat, options: [{ value: '', label: 'Todas' }, { value: 'bebidas', label: 'Bebidas' }, { value: 'postres', label: 'Postres' }] },
              { key: 'estado', label: 'Estado', value: estado, onChange: setEstado, options: [{ value: '', label: 'Todos' }, { value: 'activo', label: 'Activo' }, { value: 'inactivo', label: 'Inactivo' }] },
            ]}
            actions={<button className="adm-btn adm-btn--primary adm-btn--sm">+ Nuevo</button>}
          />
        </div>
      </CatalogGroup>
      <CatalogGroup label="Solo búsqueda (sin filters/actions)">
        <div style={{ width: '100%' }}><FilterBar search="" onSearch={() => {}} searchPlaceholder="Buscar…" /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecStatCard() {
  return (
    <CatalogBlock title="StatCard / SummaryStrip — KPIs">
      <CatalogGroup label="SummaryStrip (tira de KPIs)">
        <div style={{ width: '100%' }}>
          <SummaryStrip items={[
            { label: 'Productos', value: 118 },
            { label: 'Insumos', value: 107, delta: '+3' },
            { label: 'Sin costo', value: 12, tone: 'warn' },
            { label: 'Agotados', value: 2, tone: 'danger' },
          ]} />
        </div>
      </CatalogGroup>
      <CatalogGroup label="StatCard individual — tones">
        <StatCard label="Ventas hoy" value="$4,820" />
        <StatCard label="Margen" value="72%" tone="ok" />
        <StatCard label="Sin costo" value={12} tone="warn" />
        <StatCard label="Agotados" value={2} tone="danger" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecAuditTrail() {
  return (
    <CatalogBlock title="AuditTrail — historial de UNA entidad" note="Llama al cliente real (platform-audit); estados loading/error/vacío son los reales de la petición, no simulados.">
      <div style={{ width: '100%' }}>
        <AuditTrail entityId="catalog-demo-entity" pageSize={10} />
      </div>
    </CatalogBlock>
  )
}

// ── Inputs/Pickers ───────────────────────────────────────────────────────────

function SecEntityCombobox() {
  const [combo, setCombo] = useState<Row | null>(null)
  return (
    <CatalogBlock title="EntityCombobox — select con búsqueda async">
      <CatalogGroup>
        <EntityCombobox<Row>
          value={combo}
          onChange={setCombo}
          fetchOptions={async (q) => SAMPLE.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()))}
          renderOption={(r) => <span>{r.name} · <span className="adm-muted">{r.cat}</span></span>}
          getOptionValue={(r) => r.id}
          getOptionLabel={(r) => r.name}
          placeholder="Buscar producto…"
          allowClear
        />
        <span className="adm-muted">Sel: {combo?.name ?? '—'}</span>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecImageUploader() {
  const [img, setImg] = useState<string | null>(null)
  return (
    <CatalogBlock title="ImageUploader + ThumbnailCell">
      <CatalogGroup label="Uploader (vacío / con valor) + celda de tabla">
        <ImageUploader value={null} onChange={setImg} />
        <ImageUploader value={img} onChange={setImg} />
        <ThumbnailCell url={img} />
        <ThumbnailCell url={null} />
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecInlineEdit() {
  const [text, setText] = useState('Latte de vainilla')
  const [num, setNum] = useState<string | number>(25.5)
  const [cur, setCur] = useState<string | number>(4500)
  const { toast } = useToast()
  return (
    <CatalogBlock title="InlineEdit — valor editable in-place">
      <CatalogGroup label="type: text / number / currency">
        <div>text: <InlineEdit value={text} type="text" onSave={(v) => { setText(v); toast('Guardado', 'success') }} /></div>
        <div>number: <InlineEdit value={num} type="number" onSave={(v) => setNum(v)} /></div>
        <div>currency: <InlineEdit value={cur} type="currency" onSave={(v) => setCur(v)} /></div>
      </CatalogGroup>
      <CatalogGroup label="Estado de error (onSave rechaza)">
        <div>Forzado a fallar: <InlineEdit value="Intenta editar" onSave={async () => { throw new Error('Simulado: el servidor rechazó el cambio') }} /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecMoneyInput() {
  const [cents, setCents] = useState(4500)
  return (
    <CatalogBlock title="MoneyInput — captura de monto en MXN">
      <CatalogGroup label="Habilitado / deshabilitado">
        <MoneyInput valueCents={cents} onChangeCents={setCents} ariaLabel="Precio" />
        <MoneyInput valueCents={0} onChangeCents={() => {}} placeholder="0.00" disabled ariaLabel="Precio (deshabilitado)" />
      </CatalogGroup>
      <span className="adm-muted">Centavos: {cents}</span>
    </CatalogBlock>
  )
}

function SecSatCodePicker() {
  const [sat, setSat] = useState('50202306')
  return (
    <CatalogBlock title="SatCodePicker — clave de producto/servicio SAT">
      <CatalogGroup label="Con valor precargado / vacío (texto libre permitido)">
        <div style={{ minWidth: 260 }}><SatCodePicker value={sat} onChange={setSat} /></div>
        <div style={{ minWidth: 260 }}><SatCodePicker value="" onChange={() => {}} /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecUnitPicker() {
  const [unit, setUnit] = useState('GR')
  const [satUnit, setSatUnit] = useState('KGM')
  return (
    <CatalogBlock title="UnitPicker — combobox de unidades (favoritos + SAT)">
      <CatalogGroup label="mode='unit' bare / con label">
        <div style={{ minWidth: 220 }}><UnitPicker value={unit} onChange={setUnit} bare /></div>
        <div style={{ minWidth: 240 }}><UnitPicker value={unit} onChange={setUnit} label="Unidad de compra" /></div>
      </CatalogGroup>
      <CatalogGroup label="mode='sat' (catálogo c_ClaveUnidad completo)">
        <div style={{ minWidth: 240 }}><UnitPicker value={satUnit} onChange={setSatUnit} mode="sat" label="Clave SAT" /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecTaxRatePicker() {
  const [rate, setRate] = useState('0.16')
  const [custom, setCustom] = useState('0.12')
  return (
    <CatalogBlock title="TaxRatePicker — tasa de IVA por lista">
      <CatalogGroup label="Presets (0% / 8% / 16%) y personalizado">
        <div style={{ minWidth: 220 }}><TaxRatePicker value={rate} onChange={setRate} /></div>
        <div style={{ minWidth: 220 }}><TaxRatePicker value={custom} onChange={setCustom} label="Tasa personalizada" /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Chips/Estado ─────────────────────────────────────────────────────────────

function SecStatusPill() {
  return (
    <CatalogBlock title="StatusPill — todos los tonos">
      <CatalogGroup label="tone: ok / warn / danger / info / neutral">
        <StatusPill tone="ok" label="Activo" />
        <StatusPill tone="warn" label="Bajo stock" />
        <StatusPill tone="danger" label="Inactivo" />
        <StatusPill tone="info" label="Borrador" />
        <StatusPill tone="neutral" label="Sin costo" />
      </CatalogGroup>
      <CatalogGroup label="Helper lifecycleTone(active)">
        <StatusPill tone={lifecycleTone(true)} label="lifecycleTone(true)→ok" />
        <StatusPill tone={lifecycleTone(false)} label="lifecycleTone(false)→danger" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Feedback ─────────────────────────────────────────────────────────────────

function SecSkeleton() {
  return (
    <CatalogBlock title="Skeleton / SkeletonText — placeholders de carga">
      <CatalogGroup label="Bloque de tamaño fijo + texto multilínea">
        <Skeleton w={120} h={40} />
        <Skeleton w={40} h={40} />
        <div style={{ flex: 1, minWidth: 240 }}><SkeletonText lines={3} /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecToast() {
  const { toast } = useToast()
  return (
    <CatalogBlock title="Toast — notificación transitoria">
      <CatalogGroup label="tone: success / danger / info">
        <button className="adm-btn" onClick={() => toast('Operación exitosa', 'success')}>Toast success</button>
        <button className="adm-btn" onClick={() => toast('Algo falló', 'danger')}>Toast danger</button>
        <button className="adm-btn" onClick={() => toast('Guardando en segundo plano…', 'info')}>Toast info</button>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecFieldHelp() {
  return (
    <CatalogBlock title="FieldHelp — ayuda contextual por campo">
      <CatalogGroup label="Con doc registrado (abre Sheet con ejemplos/errores) vs. sin doc (degrada silenciosamente)">
        <FieldHelp entity="Recipe" field="name" label="Nombre de la receta" />
        <FieldHelp entity="Producto" field="campo-sin-doc" label="Campo sin ayuda" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Navegación ───────────────────────────────────────────────────────────────

function SecTabs() {
  const [tab, setTab] = useState('lista')
  return (
    <CatalogBlock title="Tabs">
      <CatalogGroup label="Con contador / sin contador">
        <div style={{ width: '100%' }}>
          <Tabs tabs={[{ id: 'lista', label: 'Lista', count: 7 }, { id: 'ficha', label: 'Ficha' }, { id: 'mods', label: 'Modificadores' }]} active={tab} onChange={setTab} ariaLabel="demo" />
          <div style={{ padding: 8, color: 'var(--pos-fg-secondary)' }}>Tab activo: <b>{tab}</b></div>
        </div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Marca ────────────────────────────────────────────────────────────────────

function SecBrandMark() {
  return (
    <CatalogBlock title="BrandMark — glifo + wordmark">
      <CatalogGroup label="size: sm / md / lg">
        <BrandMark size="sm" />
        <BrandMark size="md" />
        <BrandMark size="lg" />
      </CatalogGroup>
      <CatalogGroup label="glyphOnly / con tagline">
        <BrandMark size="md" glyphOnly />
        <BrandMark size="md" tagline="ADMIN" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecThemeSwitcher() {
  return (
    <CatalogBlock title="ThemeSwitcher — Sol ↔ Luna">
      <CatalogGroup>
        <ThemeSwitcher />
        <span className="adm-muted">Alterna [data-theme] en &lt;html&gt; — mismo componente que el topbar real.</span>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Infra ────────────────────────────────────────────────────────────────────

function Boom({ armed }: { armed: boolean }): React.ReactElement {
  if (armed) throw new Error('Error de demostración (catálogo) — esto NO es un bug real')
  return <p className="adm-muted" style={{ margin: 0 }}>Todo bien — sin errores.</p>
}

function SecErrorBoundary() {
  const [armed, setArmed] = useState(false)
  return (
    <CatalogBlock title="ErrorBoundary — recuperación de errores de render">
      <CatalogGroup label="Estado normal vs. crash forzado (con Reintentar)">
        <button className="adm-btn" onClick={() => setArmed(false)}>Restablecer demo</button>
        <button className="adm-btn adm-btn--danger" onClick={() => setArmed(true)}>Forzar error</button>
      </CatalogGroup>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <ErrorBoundary key={String(armed)}><Boom armed={armed} /></ErrorBoundary>
      </div>
    </CatalogBlock>
  )
}

function SecShareLinkButton() {
  return (
    <CatalogBlock title="ShareLinkButton — copiar enlace público">
      <CatalogGroup label="Con URL resoluble / sin slug (deshabilitado)">
        <ShareLinkButton buildUrl={({ slug }) => (slug ? `https://queue.nintaigo.com/queue/${slug}` : 'https://queue.nintaigo.com/queue/demo-slug')} label="Copiar enlace de cola" />
        <ShareLinkButton buildUrl={() => null} label="Sin slug (deshabilitado)" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Por revisar (DS-4: duplicados a unificar) ────────────────────────────────

function SecPorRevisar() {
  const cols: DataTableColumn<Row>[] = [
    { key: 'name', header: 'Nombre' },
    { key: 'price', header: 'Precio', align: 'right', render: (r) => `$${(r.price / 100).toFixed(2)}` },
  ]
  return (
    <>
      <CatalogBlock
        title="Tablas — 3 implementaciones (DS-4)"
        note="admin: DataTable (nui-table). POS: FilterableTable (nui-*) y DataTable legacy (dhk8-dt) — ver catálogo POS. Difieren en CSS namespace y forma de columnas; candidata a unificar es nui-table de FilterableTable (más completa: fechas + resumen)."
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          <DataTable<Row> columns={cols} rows={SAMPLE.slice(0, 3)} getRowKey={(r) => r.id} />
        </div>
      </CatalogBlock>

      <CatalogBlock
        title="StatusPill ↔ StatusChip"
        note="admin StatusPill: tone ok/warn/danger/info/neutral, pill con dot + label siempre visible. POS StatusChip (en FilterableTable): tone success/warning/danger/neutral, sin dot. Vocabulario de tonos distinto — unificar antes de mover a ui-restaurant."
      >
        <CatalogGroup label="admin StatusPill (referencia)">
          <StatusPill tone="ok" label="Activo" />
          <StatusPill tone="warn" label="Bajo stock" />
        </CatalogGroup>
      </CatalogBlock>

      <CatalogBlock
        title="EntityCombobox ↔ SearchSelect"
        note="admin EntityCombobox: fetch async + getOptionValue/Label + renderOption custom. POS SearchSelect: opciones síncronas (value/label/hint) + texto libre (onText) para no-inventariado. Construidos por separado — mismo propósito (combobox buscable)."
      >
        <CatalogGroup label="admin EntityCombobox (referencia)">
          <EntityCombobox<Row>
            value={null}
            onChange={() => {}}
            fetchOptions={async (q) => SAMPLE.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()))}
            renderOption={(r) => <span>{r.name}</span>}
            getOptionValue={(r) => r.id}
            getOptionLabel={(r) => r.name}
            placeholder="Buscar producto…"
          />
        </CatalogGroup>
      </CatalogBlock>

      <CatalogBlock
        title="FieldHelp ↔ HelpTip"
        note="admin FieldHelp: '?' junto al label → abre Sheet con summary/examples/commonMistakes (glosario largo). POS HelpTip: ícono (i) tap-to-toggle → burbuja portal con texto corto + mini-glosario opcional. Mismo patrón, dos APIs y dos densidades de contenido."
      >
        <CatalogGroup label="admin FieldHelp (referencia)">
          <FieldHelp entity="Recipe" field="name" label="Nombre de la receta" />
        </CatalogGroup>
      </CatalogBlock>
    </>
  )
}

// ── Registro de secciones ────────────────────────────────────────────────────

const SECTIONS: CatalogSection[] = [
  { id: 'admin-layout', label: 'AdminLayout', category: 'Layout/Shell', render: () => <SecAdminLayout /> },
  { id: 'entity-detail', label: 'EntityDetailPage', category: 'Layout/Shell', render: () => <SecEntityDetailPage /> },

  { id: 'confirm-dialog', label: 'ConfirmDialog', category: 'Overlays', render: () => <SecConfirmDialog /> },
  { id: 'sheet', label: 'Sheet', category: 'Overlays', render: () => <SecSheet /> },
  { id: 'sectioned-sheet', label: 'SectionedSheet', category: 'Overlays', render: () => <SecSectionedSheet /> },

  { id: 'data-table', label: 'DataTable', category: 'Tablas/Datos', render: () => <SecDataTable /> },
  { id: 'filter-bar', label: 'FilterBar', category: 'Tablas/Datos', render: () => <SecFilterBar /> },
  { id: 'audit-trail', label: 'AuditTrail', category: 'Tablas/Datos', render: () => <SecAuditTrail /> },
  { id: 'stat-card', label: 'StatCard / SummaryStrip', category: 'Tablas/Datos', render: () => <SecStatCard /> },

  { id: 'entity-combobox', label: 'EntityCombobox', category: 'Inputs/Pickers', render: () => <SecEntityCombobox /> },
  { id: 'image-uploader', label: 'ImageUploader', category: 'Inputs/Pickers', render: () => <SecImageUploader /> },
  { id: 'inline-edit', label: 'InlineEdit', category: 'Inputs/Pickers', render: () => <SecInlineEdit /> },
  { id: 'money-input', label: 'MoneyInput', category: 'Inputs/Pickers', render: () => <SecMoneyInput /> },
  { id: 'sat-code-picker', label: 'SatCodePicker', category: 'Inputs/Pickers', render: () => <SecSatCodePicker /> },
  { id: 'unit-picker', label: 'UnitPicker', category: 'Inputs/Pickers', render: () => <SecUnitPicker /> },
  { id: 'tax-rate-picker', label: 'TaxRatePicker', category: 'Inputs/Pickers', render: () => <SecTaxRatePicker /> },

  { id: 'status-pill', label: 'StatusPill', category: 'Chips/Estado', render: () => <SecStatusPill /> },

  { id: 'skeleton', label: 'Skeleton', category: 'Feedback', render: () => <SecSkeleton /> },
  { id: 'toast', label: 'Toast', category: 'Feedback', render: () => <SecToast /> },
  { id: 'field-help', label: 'FieldHelp', category: 'Feedback', render: () => <SecFieldHelp /> },

  { id: 'tabs', label: 'Tabs', category: 'Navegación', render: () => <SecTabs /> },

  { id: 'brand-mark', label: 'BrandMark', category: 'Marca', render: () => <SecBrandMark /> },
  { id: 'theme-switcher', label: 'ThemeSwitcher', category: 'Marca', render: () => <SecThemeSwitcher /> },

  { id: 'error-boundary', label: 'ErrorBoundary', category: 'Infra', render: () => <SecErrorBoundary /> },
  { id: 'share-link-button', label: 'ShareLinkButton', category: 'Infra', render: () => <SecShareLinkButton /> },

  { id: 'por-revisar', label: 'Duplicados (DS-4)', category: 'Por revisar', render: () => <SecPorRevisar /> },
]

export default function ComponentsGallery() {
  return (
    <CatalogShell
      title="Galería de componentes"
      subtitle="DS-3 — catálogo en vivo de la librería DS NintaiGO (admin). Compuerta de revisión antes de aplicar a las vistas."
      sections={SECTIONS}
    />
  )
}
